import type { AssessmentWizardState } from '@shared/types';
import AIResultaat from './AIResultaat';

interface Props {
  state: AssessmentWizardState;
  onMeterkastChange: (data: AssessmentWizardState['meterkast']) => void;
  onGoToStap: (stap: number) => void;
  onAfronden: () => void;
  onBack: () => void;
  isLoading: boolean;
}

const DLB_LABEL: Record<string, string> = {
  ja: 'Ja',
  nee: 'Nee',
};

function SamenvattingKaart({
  titel,
  inhoud,
  onWijzig,
}: {
  titel: string;
  inhoud: string[];
  onWijzig: () => void;
}) {
  return (
    <div className="lc-samenvatting-kaart">
      <div className="lc-samenvatting-kaart__header">
        <h3 className="lc-samenvatting-kaart__titel">{titel}</h3>
        <button
          type="button"
          className="lc-link lc-samenvatting-kaart__wijzig"
          onClick={onWijzig}
        >
          Wijzig
        </button>
      </div>
      <ul className="lc-samenvatting-kaart__lijst">
        {inhoud.map((regel, i) => (
          <li key={i}>{regel}</li>
        ))}
      </ul>
    </div>
  );
}

export default function StapSamenvatting({
  state,
  onMeterkastChange,
  onGoToStap,
  onAfronden,
  onBack,
  isLoading,
}: Props) {
  const { foto, meterkast, woning, apparaten, laadwensen } = state;

  const meterkastRegel = meterkast.aansluiting
    ? [
        `Aansluiting: ${meterkast.aansluiting}`,
        meterkast.hoofdzekering ? `Hoofdzekering: ${meterkast.hoofdzekering}` : '',
        `Groepen in gebruik: ${meterkast.aantalGroepen}`,
        meterkast.heeftAardlek !== null ? `Aardlek: ${meterkast.heeftAardlek ? 'Ja' : 'Nee'}` : '',
        foto.aiAnalyse ? 'Bron: AI-herkenning foto ✅' : 'Bron: Handmatige invoer',
      ].filter(Boolean)
    : ['(nog niet ingevuld)'];

  const woningRegel = woning.type
    ? [
        woning.type === 'anders' && woning.typeAnders
          ? `Anders: ${woning.typeAnders}`
          : woning.type.replace(/_/g, ' '),
        `${woning.oppervlakte ?? '?'} m²`,
        `Bouwjaar: ${(woning.bouwjaar ?? '').replace(/_/g, ' ')}`,
      ].filter(Boolean)
    : ['(nog niet ingevuld)'];

  const apparatenActief = [
    apparaten.inductiePlaat && `Inductiekookplaat (${apparaten.inductieVermogen ?? '?'} kW)`,
    apparaten.keramischePlaat && 'Keramische kookplaat',
    apparaten.elektrischeOven && 'Elektrische oven',
    apparaten.elektrischeBoiler && `Elektrische boiler (${apparaten.boilerInhoud ?? '?'} L)`,
    apparaten.warmtepomp && `Warmtepomp (${(apparaten.warmtepompType ?? '?').replace(/_/g, '-')})`,
    apparaten.airconditioning && `Airconditioning (${apparaten.aircoUnits ?? 1}×)`,
    apparaten.wasmachine && 'Wasmachine',
    apparaten.wasdroger && 'Wasdroger',
    apparaten.vaatwasser && 'Vaatwasser',
    apparaten.zonnepanelen && `Zonnepanelen (${apparaten.zonnepanelenVermogen ?? '?'} kWp)`,
    apparaten.thuisbatterij && `Thuisbatterij (${apparaten.thuisbatterijCapaciteit ?? '?'} kWh)`,
    apparaten.sauna && 'Sauna',
    apparaten.jacuzzi && 'Jacuzzi',
    apparaten.zwembad && 'Zwembad',
    apparaten.thuiskantoor && 'Thuiskantoor',
  ].filter(Boolean) as string[];

  const laadwensenRegel = [
    laadwensen.dlbInteresse ? `DLB: ${DLB_LABEL[laadwensen.dlbInteresse] ?? laadwensen.dlbInteresse}` : '',
    laadwensen.installatietermijn
      ? `Termijn: ${laadwensen.installatietermijn.replace(/_/g, ' ')}`
      : '',
  ].filter(Boolean);

  return (
    <div className="lc-stap lc-stap--samenvatting">
      <h2 className="lc-stap__titel">Samenvatting &amp; bevestiging</h2>
      <p className="lc-stap__beschrijving">
        Controleer je gegevens en vraag je rapport aan als alles klopt.
      </p>

      {/* AI resultaat (if available) */}
      {foto.aiAnalyse && (
        <AIResultaat
          analyse={foto.aiAnalyse}
          meterkast={meterkast}
          onMeterkastChange={onMeterkastChange}
        />
      )}

      <div className="lc-samenvatting-kaarten">
        <SamenvattingKaart
          titel="Jouw meterkast"
          inhoud={meterkastRegel}
          onWijzig={() => onGoToStap(2)}
        />
        <SamenvattingKaart
          titel="Jouw woning"
          inhoud={woningRegel}
          onWijzig={() => onGoToStap(3)}
        />
        {/* A2: positieve bevestiging i.p.v. verwarrend "Geen apparaten geselecteerd" */}
        <SamenvattingKaart
          titel="Jouw apparaten"
          inhoud={apparatenActief.length > 0 ? apparatenActief : ['Geen extra vermogensvragende apparaten aanwezig']}
          onWijzig={() => onGoToStap(4)}
        />
        <SamenvattingKaart
          titel="Jouw laadwens"
          inhoud={laadwensenRegel.length > 0 ? laadwensenRegel : ['(nog niet ingevuld)']}
          onWijzig={() => onGoToStap(5)}
        />
      </div>

      <div className="lc-betaling-cta">
        <p className="lc-betaling-cta__sub">Je ontvangt je professionele PDF-rapport per e-mail</p>
        <button
          type="button"
          className="lc-knop lc-knop--primair lc-knop--groot"
          onClick={onAfronden}
          disabled={isLoading}
        >
          {isLoading ? 'Rapport wordt aangemaakt…' : 'Rapport aanvragen'}
        </button>
      </div>

      <div className="lc-stap__acties lc-stap__acties--terug">
        <button type="button" className="lc-knop lc-knop--terug" onClick={onBack}>
          ← Terug
        </button>
      </div>
    </div>
  );
}
